import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowLeft, LinkIcon, Copy, Check,
  Sparkles, Star, ExternalLink, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  useConversation,
  useConversationRaw,
  useCellConversations,
  useToggleStarred,
} from '@/hooks/use-conversations';
import { useCellScope } from '@/hooks/use-cell-scope';
import { CellFailuresAlert } from '@/components/cells/cell-failures-alert';
import { CELLS, tenantLabel } from '@/lib/cells/cells';
import { homeCellExclusions } from '@/lib/cells/cell-statistics';
import { buildLogfireTraceUrl, buildGrafanaTraceUrl, detectLogfireProject, LOGFIRE_PROJECTS } from '@/lib/utils/format';
import {
  Page, PageContent, PageHeader, PageTitle,
} from '@/components/common/page';
import { Button } from '@/components/ui/button';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { ConversationView } from '@/components/conversations/ConversationView';
import { toEnvelope } from '@/lib/conversation-envelope';
import { AiAnalysisCard } from '@/components/conversations/ai-analysis-card';

export const Route = createFileRoute(
  '/agent/conversations/thread/$threadId',
)({
  component: ThreadDetailPage,
});

function ThreadDetailPage() {
  const { threadId } = Route.useParams();
  const [activeTab, setActiveTab] = useState('chat');
  const [copiedJson, setCopiedJson] = useState(false);

  const sourceIdentifier = `thread:${threadId}`;

  // Look up the conversation by source_identifier in every cell, each tenant
  // only in its own cell (a cell can hold copies of another's conversations).
  const { tenants, queriedCells } = useCellScope({ cellFilter: 'all' });
  const lookupQuery = useCellConversations(
    queriedCells,
    {
      source_identifier: sourceIdentifier,
      page: 1,
      page_size: 1,
      sortBy: 'conversation_date',
      sortDirection: 'desc',
    },
    (cell) => (CELLS.length > 1 ? homeCellExclusions(cell.id, tenants ?? [], []) : []),
  );
  const conversationId: string | null = lookupQuery.conversations[0]?.id ?? null;
  const tenantId: string | null = lookupQuery.conversations[0]?.tenant_id ?? null;
  const lookupLoading: boolean = lookupQuery.isLoading || queriedCells.length === 0;

  // Fetch full conversation with turns
  const { data: conversation, isLoading: isConvLoading, error } =
    useConversation(conversationId, tenantId);

  const { data: rawConversation, isLoading: isLoadingRaw } = useConversationRaw(
    conversationId, tenantId,
  );
   
  const toggleStarMutation = useToggleStarred();

  const isLoading = lookupLoading || isConvLoading;

  const tenant = tenants?.find(
    (t) => t.id === conversation?.tenant_id,
  );
  const tenantName = (tenant && tenantLabel(tenant))
    || (conversation?.tenant_id
      ? conversation.tenant_id.slice(0, 8)
      : 'Unknown');

  const displayCategory = conversation?.category
    || conversation?.ai_category || 'Uncategorized';
  const displaySubcategory = conversation?.subcategory
    || conversation?.ai_subcategory;

  const handleToggleStar = () => {
    if (!conversation || !tenantId) return;
    void toggleStarMutation.mutateAsync({
      conversationId: conversation.id,
      tenantId,
    });
  };

  const handleCopyURL = () => {
    void navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        toast.info('Thread URL copied to clipboard');
      });
  };

  const handleCopyJson = () => {
    if (!rawConversation) return;
    void navigator.clipboard
      .writeText(JSON.stringify(rawConversation, null, 2))
      .then(() => {
        setCopiedJson(true);
        setTimeout(() => { setCopiedJson(false); }, 2000);
        toast.success('Raw JSON copied to clipboard');
      });
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-destructive">
            Failed to load conversation
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            {(error).message}
          </div>
        </div>
      </div>
    );
  }

  if (!conversation && lookupQuery.failures.length > 0) {
    // A cell that didn't answer may hold the thread, so it isn't "not found".
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center p-6">
        <CellFailuresAlert
          failures={lookupQuery.failures}
          heading={(cell) => `${cell.residency} conversations didn't load - the thread may be there`}
        />
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-foreground">
            Thread not found
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            No conversation found for thread: {threadId}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <Link to="/agent/conversations">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <span
                  className="cursor-pointer hover:opacity-70 transition-opacity"
                  onClick={handleToggleStar}
                >
                  <Star
                    className={`h-5 w-5 ${conversation.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                  />
                </span>
                <PageTitle>
                  {conversation.title || threadId}
                </PageTitle>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleCopyURL}
                      className="h-8 w-8"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Copy Thread URL
                  </TooltipContent>
                </Tooltip>
                {isLoadingRaw ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center h-8 w-8">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Loading trace data...</TooltipContent>
                  </Tooltip>
                ) : rawConversation?.turns[0] ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <a
                        href={buildLogfireTraceUrl(
                          rawConversation.turns[0].trace_id,
                          detectLogfireProject(rawConversation.agent_version),
                          {
                            since: rawConversation.started_at,
                            until: rawConversation.ended_at,
                          },
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </TooltipTrigger>
                    <TooltipContent>View in Logfire</TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center justify-center h-8 w-8 text-muted-foreground/40">
                        <ExternalLink className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>No trace data</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={buildGrafanaTraceUrl(threadId, {
                        since: conversation.conversation_date,
                        until: rawConversation?.ended_at,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground"
                    >
                      <ExternalLink className="h-4 w-4 text-orange-500" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>View in Grafana</TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                <span>Tenant: {tenantName}</span>
                <span>-</span>
                <span>
                  Category: {displayCategory}
                  {displaySubcategory && ` / ${displaySubcategory}`}
                </span>
                <span>-</span>
                <span>{conversation.turn_count} messages</span>
              </div>
              {conversation.workflow_types
                && conversation.workflow_types.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {conversation.workflow_types.map((wf) => (
                    <span
                      key={wf}
                      className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded"
                    >
                      {wf.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </PageHeader>
      <PageContent>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-1 flex-col overflow-hidden h-full"
        >
          <div className="flex flex-row items-center gap-2">
            <TabsList>
              <TabsTrigger value="chat">
                Chat ({conversation.turn_count} messages)
              </TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="raw">Raw JSON</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            value="chat"
            className="flex-1 overflow-y-auto pt-4"
          >
            <ConversationView
              data={toEnvelope(conversation, rawConversation, { threadId })}
              show={['tool', 'state']}
            />
          </TabsContent>

          <TabsContent
            value="details"
            className="flex-1 overflow-y-auto pt-4"
          >
            <div className="space-y-4 max-w-4xl">
              <div className="rounded-lg border bg-card p-4">
                <h4 className="font-semibold mb-3">
                  Categorization
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-2">
                      <Sparkles className="w-3 h-3 text-purple-500" />
                      AI Generated
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">
                          Category:
                        </span>{' '}
                        <span className="text-purple-700">
                          {conversation.ai_category || 'None'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">
                          Subcategory:
                        </span>{' '}
                        <span className="text-purple-600">
                          {conversation.ai_subcategory || 'None'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      Manual Annotation
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="font-medium">
                          Category:
                        </span>{' '}
                        {conversation.category || 'None'}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">
                          Subcategory:
                        </span>{' '}
                        {conversation.subcategory || 'None'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <AiAnalysisCard
                sentiment={conversation.sentiment}
                aiSentiment={conversation.ai_sentiment}
                categoryReasoning={
                  conversation.category_reasoning
                }
                interestLevel={conversation.interest_level}
                interestLevelReasoning={
                  conversation.interest_level_reasoning
                }
                resolution={conversation.resolution}
                resolutionReasoning={
                  conversation.resolution_reasoning
                }
              />

              <div className="rounded-lg border bg-card p-4">
                <h4 className="font-semibold mb-3">
                  Conversation Details
                </h4>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">ID:</dt>
                    <dd className="font-mono">
                      {conversation.id}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">
                      Thread ID:
                    </dt>
                    <dd className="font-mono">{threadId}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">
                      Tenant ID:
                    </dt>
                    <dd className="font-mono">
                      {conversation.tenant_id}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">
                      Date:
                    </dt>
                    <dd>
                      {new Date(
                        conversation.conversation_date,
                      ).toLocaleString()}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">
                      Messages:
                    </dt>
                    <dd>{conversation.turn_count}</dd>
                  </div>
                  {conversation.initiated_by_employee_name && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">
                        Initiated By:
                      </dt>
                      <dd>
                        {conversation.initiated_by_employee_name}
                      </dd>
                    </div>
                  )}
                  {'source' in conversation
                    && conversation.source && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">
                        Source:
                      </dt>
                      <dd className="font-semibold capitalize">
                        {conversation.source}
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">
                      Source ID:
                    </dt>
                    <dd className="font-mono text-xs">
                      {sourceIdentifier}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Logfire Traces Section */}
              {rawConversation?.turns && rawConversation.turns.length > 0 && (() => {
                const detectedProject = detectLogfireProject(rawConversation.agent_version);
                const otherKeys = (Object.keys(LOGFIRE_PROJECTS) as (keyof typeof LOGFIRE_PROJECTS)[]).filter(k => k !== detectedProject);
                return (
                  <div className="rounded-lg border bg-card p-4">
                    <h4 className="font-semibold mb-3">Logfire Traces</h4>
                    <div className="space-y-3">
                      {rawConversation.turns.map((turn) => (
                        <div key={turn.trace_id} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground shrink-0">
                              Turn {turn.turn_number}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground truncate" title={turn.trace_id}>
                              {turn.trace_id}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                void navigator.clipboard.writeText(turn.trace_id).then(() => {
                                  toast.info('Trace ID copied to clipboard');
                                });
                              }}
                              className="p-1 hover:bg-muted rounded transition-colors shrink-0"
                            >
                              <Copy className="h-3 w-3 text-muted-foreground" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 ml-1">
                            <a
                              href={buildLogfireTraceUrl(turn.trace_id, detectedProject, {
                                since: turn.timestamp,
                                until: turn.agent_responses.at(-1)?.end_timestamp,
                              })}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {/* eslint-disable-next-line security/detect-object-injection */}
                              {LOGFIRE_PROJECTS[detectedProject]}
                            </a>
                            {otherKeys.map((key) => (
                              <a
                                key={key}
                                href={buildLogfireTraceUrl(turn.trace_id, key, {
                                  since: turn.timestamp,
                                  until: turn.agent_responses.at(-1)?.end_timestamp,
                                })}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                {/* eslint-disable-next-line security/detect-object-injection */}
                                {LOGFIRE_PROJECTS[key]}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          <TabsContent
            value="raw"
            className="flex-1 overflow-y-auto pt-4"
          >
            <div className="rounded-lg border bg-card p-4 relative">
              <Button
                variant="outline"
                size="icon"
                className="absolute top-4 right-4 h-8 w-8"
                onClick={handleCopyJson}
              >
                {copiedJson
                  ? <Check className="h-4 w-4" />
                  : <Copy className="h-4 w-4" />}
              </Button>
              <pre className="text-xs overflow-auto pr-24">
                {rawConversation
                  ? JSON.stringify(rawConversation, null, 2)
                  : 'Loading raw JSON...'}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  )
}
